// @mui
import { styled } from '@mui/material/styles';
import { Avatar, Box, Typography } from '@mui/material';
// utils
import { fToNowStrict } from '../../../utils/formatTime';
import { campaignImage, chatImage } from '../../../utils/images';
// hooks
import { useLocales } from '../../../locales';

// components
import Image from '../../../components/image';
import Iconify from '../../../components/iconify';

// redux
import { useSelector } from '../../../redux/store';
import { ConversationItemForUserChat, MessageToSendItemForChat } from '../../../redux/coreApi';
import { selectCurrentUser } from '../../../redux/slices/auth';
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(3),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 320,
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
}));

const InfoStyle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(0.75),
  color: theme.palette.text.secondary,
}));

// ----------------------------------------------------------------------

type Props = {
  message: MessageToSendItemForChat;
  conversation: ConversationItemForUserChat;
  onOpenLightbox: (value: string) => void;
};

export default function ChatMessageItem({
  message,
  conversation,
  onOpenLightbox,
}: Readonly<Props>) {
  const {
    currentLang: { value },
  } = useLocales();
  const user = useSelector(selectCurrentUser);

  const { senderId = '', message: body = '', attachments = [], createdAt } = message;

  const { id: conversationId, brandName, brandLogo } = conversation;
  const isMe = senderId === user?.id;
  const isCoach = conversationId === 'tribz-coach';

  const link = (text: string) => (text.startsWith('http') ? text : `https://${text}`);

  const urlify = (text: string) => {
    const urlRegex =
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
    return text.split(urlRegex).map((part) => {
      if (part.match(urlRegex)) {
        return (
          <a key={part} href={link(part)}>
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const formatMessage = (text: string) => urlify(text);

  const isAudioFile = (url: string) =>
    url.startsWith('blob:') || /\.(webm|ogg|mp3|wav|m4a)$/i.test(url);

  const isImageFile = (url: string) =>
    /\.(jpg|jpeg|png|gif|webp)$/i.test(url) && !url.startsWith('blob:');

  return (
    <RootStyle>
      <Box
        sx={{
          display: 'flex',
          ...(isMe && {
            ml: 'auto',
          }),
        }}
      >
        {!isMe && (
          <Avatar
            alt={brandName}
            src={
              isCoach
                ? undefined
                : brandLogo?.startsWith('/') || brandLogo?.startsWith('http')
                ? brandLogo
                : campaignImage(brandLogo)
            }
            sx={{ width: 32, height: 32, mr: 2 }}
          >
            {isCoach && <Iconify icon="ic:round-assistant" width={20} height={20} />}
          </Avatar>
        )}

        <div>
          <InfoStyle
            variant="caption"
            sx={{
              ...(isMe && { justifyContent: 'flex-end' }),
            }}
          >
            {!isMe && `${brandName},`}&nbsp;
            {createdAt ? fToNowStrict(new Date(createdAt), value) : ''}
          </InfoStyle>

          {!!body.length && (
            <ContentStyle
              sx={{
                ...(isMe && { color: 'grey.800', bgcolor: 'primary.lighter' }),
              }}
            >
              <Typography variant="body2">{formatMessage(body)}</Typography>
            </ContentStyle>
          )}

          {attachments.length > 0 && (
            <ContentStyle
              sx={{
                ...(isMe && { color: 'grey.800', bgcolor: 'primary.lighter' }),
                ...{ p: 1.5 },
              }}
            >
              {attachments.map((attachment) => {
                const attachmentUrl = chatImage(attachment);
                if (isAudioFile(attachment)) {
                  return (
                    <Box key={attachment} sx={{ mb: 1 }}>
                      <audio
                        controls
                        src={attachmentUrl}
                        style={{ maxWidth: '100%', height: 32 }}
                      />
                    </Box>
                  );
                }
                if (isImageFile(attachment)) {
                  return (
                    <Image
                      key={attachment}
                      alt={attachment}
                      src={attachmentUrl}
                      onClick={() => onOpenLightbox(attachmentUrl)}
                      sx={{
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 },
                        mb: 1,
                      }}
                    />
                  );
                }
                return null;
              })}
            </ContentStyle>
          )}

          {/* {contentTypes.includes('video') && attachments.length > 0 && (
            <ContentStyle
              sx={{
                ...(isMe && { color: 'grey.800', bgcolor: 'primary.lighter' }),
                ...{ p: 0 },
              }}
            >
              {attachments.map((file, index) => (
                <video key={index} controls muted>
                  <source src={file} type="video/mp4" />
                </video>
              ))}
            </ContentStyle>
          )}*/}
        </div>
      </Box>
    </RootStyle>
  );
}
